import { useEffect, useRef } from "react";
import * as THREE from "three";
import { CubeLut, sampleLut, Vec3 } from "./lut";

interface ColorCubeProps {
  lut: CubeLut | null;
}

export function ColorCube({ lut }: ColorCubeProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
    camera.position.set(2.6, 2.0, 3.2);
    camera.lookAt(0, 0, 0);

    const group = new THREE.Group();
    scene.add(group);

    const box = new THREE.BoxGeometry(2, 2, 2);
    const edges = new THREE.EdgesGeometry(box);
    const line = new THREE.LineSegments(
      edges,
      new THREE.LineBasicMaterial({ color: 0x44505f, transparent: true, opacity: 0.85 })
    );
    group.add(line);

    const grid = createGrid();
    group.add(grid);

    const points = createPointCloud(lut);
    group.add(points);

    const resize = () => {
      const rect = mount.getBoundingClientRect();
      renderer.setSize(rect.width, rect.height);
      camera.aspect = rect.width / Math.max(rect.height, 1);
      camera.updateProjectionMatrix();
    };

    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);

    let frame = 0;
    const animate = () => {
      frame = requestAnimationFrame(animate);
      group.rotation.y += 0.003;
      group.rotation.x = Math.sin(Date.now() * 0.00035) * 0.08;
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      mount.removeChild(renderer.domElement);
      points.geometry.dispose();
      (points.material as THREE.Material).dispose();
      box.dispose();
      edges.dispose();
      renderer.dispose();
    };
  }, [lut]);

  return <div className="cubeStage" ref={mountRef} />;
}

function createGrid() {
  const group = new THREE.Group();
  const material = new THREE.LineBasicMaterial({ color: 0x202a36, transparent: true, opacity: 0.55 });
  const positions = [-1, -0.5, 0, 0.5, 1];

  for (const p of positions) {
    group.add(line([-1, p, -1], [1, p, -1], material));
    group.add(line([p, -1, -1], [p, 1, -1], material));
    group.add(line([-1, -1, p], [1, -1, p], material));
    group.add(line([p, -1, -1], [p, -1, 1], material));
  }

  return group;
}

function line(a: Vec3, b: Vec3, material: THREE.LineBasicMaterial) {
  const geometry = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(a[0], a[1], a[2]),
    new THREE.Vector3(b[0], b[1], b[2])
  ]);
  return new THREE.Line(geometry, material);
}

function createPointCloud(lut: CubeLut | null) {
  const steps = lut ? Math.min(lut.dimension, 17) : 9;
  const positions: number[] = [];
  const colors: number[] = [];

  for (let z = 0; z < steps; z++) {
    for (let y = 0; y < steps; y++) {
      for (let x = 0; x < steps; x++) {
        const input: Vec3 = [
          x / (steps - 1),
          y / (steps - 1),
          z / (steps - 1)
        ];
        const mapped = lut ? sampleLut(input, lut) : input;
        positions.push(mapped[0] * 2 - 1, mapped[1] * 2 - 1, mapped[2] * 2 - 1);
        colors.push(mapped[0], mapped[1], mapped[2]);
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));

  const material = new THREE.PointsMaterial({
    size: 0.026,
    vertexColors: true,
    transparent: true,
    opacity: 0.94,
    depthWrite: false
  });

  return new THREE.Points(geometry, material);
}
